import { CSSProperties, useMemo } from 'react';
import { Easing, interpolate, InterpolateOptions, useCurrentFrame, useVideoConfig } from 'remotion';
import { isEmpty } from 'radash';
import { ImageElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import {
  LifeCycleTransition,
  TransitionLifeCycleType,
  TransitionType
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.types';
import {
  TransformConfig,
  TRANSITION_MINIMUM_DURATION
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.constants';
import { getElementDurationInMs } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transition.utils';

const noStyles: CSSProperties = {};

export const useTransitionStyles = ({ element }: { element: ImageElement | VideoElement }): CSSProperties => {
  const currentFrame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const { transitions } = element;

  const totalFrames = useMemo(() => {
    return element.toFrame - element.fromFrame;
  }, [element.toFrame, element.fromFrame]);

  const parentDuration = useMemo(() => {
    return getElementDurationInMs(element, fps);
  }, [element, fps]);

  const applicableTransitions = useMemo(() => {
    const validTransitions: { transition: LifeCycleTransition; lifeCycle: TransitionLifeCycleType }[] = [];
    if (!transitions || parentDuration < TRANSITION_MINIMUM_DURATION) {
      return validTransitions;
    }

    const { in: inTransition, out: outTransition } = transitions;

    if (outTransition && outTransition.properties.duration >= TRANSITION_MINIMUM_DURATION) {
      validTransitions.push({ transition: outTransition, lifeCycle: TransitionLifeCycleType.OUT });
    }
    if (inTransition && inTransition.properties.duration >= TRANSITION_MINIMUM_DURATION) {
      validTransitions.push({ transition: inTransition, lifeCycle: TransitionLifeCycleType.IN });
    }
    return validTransitions;
  }, [transitions, parentDuration]);

  if (applicableTransitions.length === 0) {
    return noStyles;
  }

  let opacity = null as CSSProperties['opacity'] | null;
  let transform = null as CSSProperties['transform'] | null;

  applicableTransitions.forEach(({ transition, lifeCycle }) => {
    const durationInSeconds = transition.properties.duration / 1000;
    const startFrame = lifeCycle === TransitionLifeCycleType.IN ? 0 : totalFrames - durationInSeconds * fps;
    const endFrame = lifeCycle === TransitionLifeCycleType.IN ? durationInSeconds * fps : totalFrames;
    const inputRange = [startFrame, endFrame];
    const easing = lifeCycle === TransitionLifeCycleType.IN ? Easing.out(Easing.quad) : Easing.in(Easing.quad);
    const interpolateConfig: InterpolateOptions = {
      easing,
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    };

    if (durationInSeconds > 0 && currentFrame >= inputRange[0] && currentFrame <= inputRange[1]) {
      const opacityRange = TransformConfig[TransitionType.FADE][lifeCycle].opacity;
      opacity = interpolate(currentFrame, inputRange, opacityRange, interpolateConfig);
      if (transition.type === TransitionType.SCALE) {
        const outputScaleRange = TransformConfig[transition.type][lifeCycle].scale;
        const scaleValue = interpolate(currentFrame, inputRange, outputScaleRange, interpolateConfig);
        transform = `scale(${scaleValue})`;
      }
      if (transition.type === TransitionType.DROP) {
        const outputScaleRange = TransformConfig[transition.type][lifeCycle].scale;
        const scaleValue = interpolate(currentFrame, inputRange, outputScaleRange, interpolateConfig);
        transform = `scale(${scaleValue})`;
      }
      if (transition.type === TransitionType.ROTATE) {
        const { rotateDirection } = transition.properties;
        const outputRotateRange = TransformConfig[transition.type][lifeCycle][rotateDirection].rotate;
        const rotateValue = interpolate(currentFrame, inputRange, outputRotateRange, interpolateConfig);

        transform = `rotate(${rotateValue}deg)`;
      }
      if (transition.type === TransitionType.MOVE) {
        const outputMoveRange = TransformConfig[transition.type][lifeCycle][transition.properties.moveDirection];

        const translateXRange = outputMoveRange.translateX.map(i => i * width);
        const translateYRange = outputMoveRange.translateY.map(i => i * height);
        const translateXValue = interpolate(currentFrame, inputRange, translateXRange, interpolateConfig);
        const translateYValue = interpolate(currentFrame, inputRange, translateYRange, interpolateConfig);

        transform = `translate(${translateXValue}px, ${translateYValue}px)`;
      }
    }
  });

  const styles: CSSProperties = {};
  if (opacity !== null) {
    styles.opacity = opacity;
  }
  if (transform !== null) {
    styles.transform = transform;
  }

  return isEmpty(styles) ? noStyles : styles;
};
